import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import axios from 'axios'
import { RootState } from '@/store'
import { AxiosList, Data } from '@/modules/common/apiConfig'
import {
	SecureMessage,
	SecureMessageAttributes,
} from '@/modules/secureMessages/types/secureMessageTypes'

export type State = {
	messages: SecureMessage[]
	openMessage: SecureMessage | null
	unreadCount: number
	loading: boolean
}

export const state = (): State => ({
	messages: [],
	openMessage: null,
	unreadCount: 0,
	loading: false,
})

const mutations: MutationTree<State> = {
	setMessages(state: State, messages: SecureMessage[]) {
		state.messages = messages
	},
	setOpenMessage(state: State, message: SecureMessage | null) {
		state.openMessage = message
	},
	setUnreadCount(state: State, unreadCount: number) {
		state.unreadCount = unreadCount
	},
	setLoading(state: State, loading: boolean) {
		state.loading = loading
	},
	markRead(state: State, id: string) {
		const readAt = new Date().toISOString()

		state.messages = state.messages.map(message =>
			message.id === id ? { ...message, read_at: message.read_at ?? readAt } : message,
		)

		if (state.openMessage?.id === id) {
			state.openMessage = { ...state.openMessage, read_at: state.openMessage.read_at ?? readAt }
		}
	},
	decrementUnreadCount(state: State) {
		state.unreadCount = Math.max(0, state.unreadCount - 1)
	},
}

function toMessage(resource: Data<SecureMessageAttributes>): SecureMessage {
	return { id: resource.id, ...resource.attributes }
}

const actions: ActionTree<State, RootState> = {
	async getMessages({ commit }): Promise<void> {
		try {
			commit('setLoading', true)

			// The response interceptor unwraps the HTTP body, leaving `{ data, meta }`.
			const { data } = (await axios.get(
				'/restify/secure-messages',
			)) as unknown as AxiosList<SecureMessageAttributes>

			commit('setMessages', data.map(toMessage))
		} catch (err: any) {
			if (err.handled) {
				return
			}

			throw err
		} finally {
			commit('setLoading', false)
		}
	},
	async getMessage({ commit }, id: string): Promise<void> {
		try {
			commit('setLoading', true)
			commit('setOpenMessage', null)

			const { data } = (await axios.get(`/restify/secure-messages/${id}`)) as unknown as {
				data: Data<SecureMessageAttributes>
			}

			commit('setOpenMessage', toMessage(data))
		} catch (err: any) {
			if (err.handled) {
				return
			}

			throw err
		} finally {
			commit('setLoading', false)
		}
	},
	async getUnreadCount({ commit }): Promise<void> {
		const { data } = (await axios.get('/restify/secure-messages/getters/unread-count', {
			// A bell that can't load its count is not worth a toast over the page the client asked for.
			showsErrorsItself: true,
		} as any)) as unknown as { data: { count: number } }

		commit('setUnreadCount', data.count)
	},
	async markRead({ commit, state }, id: string): Promise<void> {
		const message = state.openMessage?.id === id ? state.openMessage : null

		if (message?.read_at) {
			return
		}

		await axios.post(`/restify/secure-messages/${id}/actions?action=mark-as-read`)

		commit('markRead', id)
		commit('decrementUnreadCount')
	},
}

const getters: GetterTree<State, RootState> = {
	messages: (state: State) => state.messages,
	openMessage: (state: State) => state.openMessage,
	unreadCount: (state: State) => state.unreadCount,
	hasUnread: (state: State) => state.unreadCount > 0,
	loading: (state: State) => state.loading,
}

const module: Module<State, RootState> = {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
}

export default module
